import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import api, { getImageUrl } from "../../utils/api";
import Loader from "../../components/common/Loader";
import { PERMISSIONS, canFeatureForRole } from "../../utils/permissions";
import { useRolePermissionMatrix } from "../../hooks/useRolePermissionMatrix";
import Pagination from "../../components/common/Pagination";
import BadgeStatus from "../../components/common/BadgeStatus";
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from "../../utils/settings";
import "../../styles/Table.css";

const MyAssetsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const normalizedUserRole = useMemo(() => {
    try {
      const u = JSON.parse(sessionStorage.getItem("user") || "{}");
      return String(u?.role || "").trim().toLowerCase();
    } catch {
      return "";
    }
  }, []);
  const canViewAssignedAssets = useMemo(
    () =>
      canFeatureForRole(normalizedUserRole, rolePermissions, [
        PERMISSIONS.VIEW_ASSIGNED_ASSETS,
      ]),
    [normalizedUserRole, rolePermissions]
  );

  useEffect(() => {
    if (!permissionsReady) return;
    if (!canViewAssignedAssets) {
      toast.error("You do not have permission to view assigned assets");
      const dash = location.pathname.startsWith("/admin")
        ? "/admin/dashboard"
        : "/employee/dashboard";
      navigate(dash, { replace: true });
    }
  }, [permissionsReady, canViewAssignedAssets, navigate, location.pathname]);

  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());
  
  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);
  const [totalAssets, setTotalAssets] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("assetTag"); // Default sort by Tag ID
  const [sortOrder, setSortOrder] = useState("asc"); // Default ascending
  const [pendingTransfers, setPendingTransfers] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState({
    image: true,
    assetTag: true,
    description: true,
    brand: true,
    purchaseDate: false,
    cost: false,
    status: true,
    purchasedFrom: false,
    model: false,
    serialNumber: false,
    site: true,
    location: true,
    category: true,
    department: true,
    assignedTo: false,
    createdAt: false,
    action: true,
  });

  useEffect(() => {
    const userStr = sessionStorage.getItem("user");
    if (userStr) {
      const user = JSON.parse(userStr);
      setCurrentUser(user);
    }

    // Refresh assets when page comes back into focus (after acknowledgment or transfer)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchMyAssets();
      }
    };

    // Refresh when window gains focus (user returns to tab)
    const handleFocus = () => {
      fetchMyAssets();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  // Refresh data when returning to this page from navigation
  useEffect(() => {
    if (!permissionsReady || !canViewAssignedAssets) return;
    // Only refresh if we're on the MyAssetsPage route
    if (
      location.pathname === "/employee/assets" ||
      location.pathname === "/employee/my-assets" ||
      location.pathname === "/admin/my-assets"
    ) {
      const timer = setTimeout(() => {
        fetchMyAssets();
      }, 100); // Small delay to ensure navigation is complete

      return () => clearTimeout(timer);
    }
  }, [location.pathname, permissionsReady, canViewAssignedAssets]);

  // Fetch assets when pagination, filter, or sorting changes
  useEffect(() => {
    if (!permissionsReady || !canViewAssignedAssets) return;
    fetchMyAssets();
  }, [currentPage, itemsPerPage, statusFilter, sortBy, sortOrder, permissionsReady, canViewAssignedAssets]);

  const fetchMyAssets = async () => {
    if (!permissionsReady || !canViewAssignedAssets) return;
    try {
      setLoading(true);
      const userStr = sessionStorage.getItem("user");
      if (!userStr) return;

      const user = JSON.parse(userStr);
      const userId = user.id || user._id;

      // Build query parameters with pagination
      const assetsParams = {
        assignedTo: userId,
        page: currentPage,
        limit: itemsPerPage,
      };

      // Add status filter only if not "all"
      if (statusFilter !== "all") {
        assetsParams.status = statusFilter;
      }

      // Add sorting parameters
      assetsParams.sortBy = sortBy;
      assetsParams.sortOrder = sortOrder;

      // Fetch assets and pending transfers in parallel
      const [assetsResponse, handoversResponse] = await Promise.all([
        api.getAssets(assetsParams),
        api.getHandovers({ acknowledgementStatus: "pending" }),
      ]);

      if (assetsResponse.success) {
        const fetchedAssets = assetsResponse.data || [];
        setAssets(fetchedAssets);

        // Set pagination data from backend response
        if (assetsResponse.total !== undefined) {
          setTotalAssets(assetsResponse.total);
        } else {
          setTotalAssets(fetchedAssets.length);
        }

        if (assetsResponse.totalPages !== undefined) {
          setTotalPages(assetsResponse.totalPages);
        } else {
          setTotalPages(
            Math.ceil(
              (assetsResponse.total || fetchedAssets.length) / itemsPerPage
            )
          );
        }

        // Process pending transfers - store with asset ID as string key
        const transfers = {};
        if (handoversResponse.success && handoversResponse.data) {
          // Create a map of user's asset IDs (as strings) for quick lookup
          const userAssetIdMap = {};
          fetchedAssets.forEach((asset) => {
            const id = asset._id || asset.id;
            if (id) {
              const idStr = id.toString();
              userAssetIdMap[idStr] = asset;
            }
          });

          handoversResponse.data.forEach((handover) => {
            if (handover.handoverType === "transfer") {
              // Handle different asset formats from API response
              let handoverAssetId = null;
              if (handover.asset) {
                // Asset might be an object with _id, or just an _id string/ObjectId
                handoverAssetId =
                  handover.asset._id || handover.asset.id || handover.asset;
              }

              if (handoverAssetId) {
                const handoverAssetIdStr = handoverAssetId.toString();
                // If this transfer is for one of the user's assets, store it
                if (userAssetIdMap[handoverAssetIdStr]) {
                  // Store with the asset's ID as string key
                  transfers[handoverAssetIdStr] = handover;
                }
              }
            }
          });
        }
        setPendingTransfers(transfers);
      }
    } catch (error) {
      console.error("Error fetching my assets:", error);
      toast.error("Error fetching assets");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if clicking the same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new sort field and default to ascending
      setSortBy(field);
      setSortOrder("asc");
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  // Helper function to render sortable header
  const renderSortableHeader = (field, label, className = "") => {
    const isActive = sortBy === field;
    const isAsc = sortOrder === "asc";

    return (
      <th
        className={`table-header-cell ${className}`}
        onClick={() => handleSort(field)}
        style={{
          cursor: "pointer",
          userSelect: "none",
          position: "relative",
          ...(isActive && { color: "#667eea" }),
        }}
        title={`Click to sort by ${label}`}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <span>{label}</span>
          {isActive && (
            <span style={{ fontSize: "10px", color: "#667eea" }}>
              {isAsc ? "↑" : "↓"}
            </span>
          )}
          {!isActive && (
            <span
              style={{ fontSize: "10px", color: "#cbd5e0", opacity: 0.5 }}
            >
              ⇅
            </span>
          )}
        </div>
      </th>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleTransferClick = (assetId) => {
    // Find the asset and navigate to the transfer form with it pre-selected
    const asset = assets.find((a) => (a._id || a.id) === assetId);
    if (asset) {
      // Determine the base path based on current location
      const isAdmin = location.pathname.startsWith('/admin');
      const transferPath = isAdmin ? "/admin/transfer-assets/form" : "/employee/transfer-assets/form";
      navigate(transferPath, {
        state: { selectedAssets: [asset] },
      });
    }
  };

  const handleBulkTransferClick = () => {
    if (selectedAssets.length === 0) {
      toast.error("Please select at least one asset to transfer");
      return;
    }

    // Get selected asset objects
    const selectedAssetObjects = assets.filter((asset) =>
      selectedAssets.includes(asset._id || asset.id)
    );

    // Filter to only include transferable assets (received or available, not in_transfer)
    // Backend will validate if there's already a pending transfer
    const transferableAssets = selectedAssetObjects.filter((asset) => {
      const assetId = asset._id || asset.id;
      if (!assetId) return false;

      return (
        (asset.status === "received" || asset.status === "available") &&
        asset.status !== "in_transfer"
      );
    });

    if (transferableAssets.length === 0) {
      toast.error(
        'No transferable assets selected. Assets must be in "received" or "available" status and not already in transfer.'
      );
      return;
    }

    // Determine the base path based on current location
    const isAdmin = location.pathname.startsWith('/admin');
    const transferPath = isAdmin ? "/admin/transfer-assets/form" : "/employee/transfer-assets/form";
    navigate(transferPath, {
      state: { selectedAssets: transferableAssets },
    });
  };

  const handleToggleAsset = (asset) => {
    const assetId = asset._id || asset.id;
    const isSelected = selectedAssets.includes(assetId);

    if (isSelected) {
      setSelectedAssets(selectedAssets.filter((id) => id !== assetId));
    } else {
      setSelectedAssets([...selectedAssets, assetId]);
    }
  };

  const handleSelectAll = () => {
    const allSelected = assets.every((asset) =>
      selectedAssets.includes(asset._id || asset.id)
    );

    if (allSelected) {
      // Deselect all current page assets
      const currentPageIds = assets.map((a) => a._id || a.id);
      setSelectedAssets(
        selectedAssets.filter((id) => !currentPageIds.includes(id))
      );
    } else {
      // Select all current page assets (avoid duplicates)
      const currentPageIds = assets.map((a) => a._id || a.id);
      const newIds = currentPageIds.filter(
        (id) => !selectedAssets.includes(id)
      );
      setSelectedAssets([...selectedAssets, ...newIds]);
    }
  };

  const getTransferStatusBadge = (assetId) => {
    const transfer = pendingTransfers[assetId];
    if (transfer) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 12px",
            backgroundColor: "#fed7d7",
            borderRadius: "6px",
          }}
        >
          <span
            style={{
              padding: "4px 12px",
              borderRadius: "12px",
              fontSize: "11px",
              fontWeight: 600,
              backgroundColor: "#fed7d7",
              color: "#c53030",
              display: "inline-block",
            }}
          >
            ⏳ In Transfer
          </span>
          <span style={{ fontSize: "11px", color: "#742a2a" }}>
            to {transfer.assignedTo?.name || "Unknown"}
          </span>
        </div>
      );
    }
    return null;
  };

  if (!permissionsReady || !canViewAssignedAssets) {
    return <Loader />;
  }

  if (loading && assets.length === 0) {
    return <Loader />;
  }

  return (
    <div className="my-assets-page" style={{ padding: "16px" }}>
      {assets.length === 0 ? (
        <div
          style={{
            background: "white",
            padding: "60px",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>📦</div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: 600,
              color: "#2d3748",
            }}
          >
            No Assets Assigned
          </h3>
          <p style={{ margin: 0, color: "#718096", fontSize: "13px" }}>
            You don't have any assets assigned to you at this time.
          </p>
        </div>
      ) : (
        <div
          style={{
            background: "white",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
              gap: "12px",
            }}
          >
            <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>
              My Assets
            </h2>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <label
                  htmlFor="statusFilter"
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#4a5568",
                  }}
                >
                  Filter:
                </label>
                <select
                  id="statusFilter"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                    setSelectedAssets([]);
                  }}
                  style={{
                    padding: "6px 12px",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#2d3748",
                    backgroundColor: "white",
                    cursor: "pointer",
                    minWidth: "150px",
                  }}
                >
                  <option value="received">Received</option>
                  <option value="pending">Pending</option>
                  <option value="issue_reported">Issue Reported</option>
                  <option value="all">All</option>
                </select>
              </div>
            </div>
          </div>
          <div className="scrollable-table-container">
            <table>
              <thead>
                <tr className="table-header-row">
                  <th className="table-header-cell table-header-cell-checkbox">
                    <input
                      type="checkbox"
                      checked={
                        assets.length > 0 &&
                        assets.every((asset) =>
                          selectedAssets.includes(asset._id || asset.id)
                        )
                      }
                      onChange={handleSelectAll}
                      className="table-checkbox"
                    />
                  </th>
                  <th className="table-header-cell table-header-cell-serial">
                    S.No.
                  </th>
                  {visibleColumns.image && (
                    <th className="table-header-cell">Image</th>
                  )}
                  {visibleColumns.assetTag &&
                    renderSortableHeader("assetTag", "Asset Tag")}
                  {visibleColumns.description &&
                    renderSortableHeader(
                      "description",
                      "Description",
                      "table-header-cell-description"
                    )}
                  {visibleColumns.brand &&
                    renderSortableHeader(
                      "brand",
                      "Brand",
                      "table-header-cell-brand"
                    )}
                  {visibleColumns.status &&
                    renderSortableHeader("status", "Status")}
                  {visibleColumns.site && (
                    <th className="table-header-cell table-header-cell-location">
                      Site
                    </th>
                  )}
                  {visibleColumns.category &&
                    renderSortableHeader("category", "Category")}
                  <th className="table-header-cell table-header-cell-actions">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {assets.map((asset, index) => {
                  const assetId = asset._id || asset.id;
                  const isSelected = selectedAssets.includes(assetId);
                  const serialNumber =
                    (currentPage - 1) * itemsPerPage + index + 1;

                  // Show transfer button for all assets with received/available status
                  // Backend will validate if there's already a pending transfer
                  const canTransfer =
                    assetId &&
                    (asset.status === "received" ||
                      asset.status === "available") &&
                    asset.status !== "in_transfer";

                  return (
                    <React.Fragment key={asset._id}>
                      <tr
                        className={`table-body-row ${
                          isSelected
                            ? "table-body-row-selected-blue"
                            : index % 2 === 1
                            ? "table-body-row-alternate"
                            : ""
                        }`}
                      >
                        <td className="table-body-cell-checkbox">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleAsset(asset)}
                            className="table-checkbox"
                          />
                        </td>
                        <td className="table-body-cell-serial">
                          {serialNumber}
                        </td>
                        {visibleColumns.image && (
                          <td className="table-body-cell">
                            {asset.photos && asset.photos.length > 0 ? (
                              <img
                                src={getImageUrl(asset.photos[0].url)}
                                alt={asset.description}
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "4px",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  borderRadius: "4px",
                                  backgroundColor: "#e2e8f0",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "10px",
                                  color: "#718096",
                                }}
                              >
                                📦
                              </div>
                            )}
                          </td>
                        )}
                        {visibleColumns.assetTag && (
                          <td className="table-body-cell-asset-tag">
                            {asset.assetTag}
                          </td>
                        )}
                        {visibleColumns.description && (
                          <td className="table-body-cell-description">
                            {asset.description}
                          </td>
                        )}
                        {visibleColumns.brand && (
                          <td className="table-body-cell-brand">
                            {asset.brand || "N/A"}
                          </td>
                        )}
                        {visibleColumns.status && (
                          <td className="table-body-cell">
                            <BadgeStatus status={asset.status} />
                          </td>
                        )}
                        {visibleColumns.site && (
                          <td className="table-body-cell-location">
                            {asset.location?.site?.name || "N/A"}
                          </td>
                        )}
                        {visibleColumns.category && (
                          <td className="table-body-cell-default">
                            {asset.category || "N/A"}
                          </td>
                        )}
                        <td className="table-body-cell-actions">
                          <div className="table-actions-container">
                            <button
                              type="button"
                              onClick={() => {
                                // Determine the base path based on current location
                                const isAdmin = location.pathname.startsWith('/admin');
                                const assetPath = isAdmin ? `/admin/assets/${assetId}` : `/employee/assets/${assetId}`;
                                navigate(assetPath);
                              }}
                              style={{
                                padding: "6px 10px",
                                background: "transparent",
                                color: "#667eea",
                                border: "1px solid #667eea",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontWeight: 600,
                                fontSize: "12px",
                                transition: "all 0.2s",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = "#667eea";
                                e.target.style.color = "white";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = "transparent";
                                e.target.style.color = "#667eea";
                              }}
                              title="View Details"
                            >
                              👁️ View
                            </button>
                            {canTransfer && (
                              <button
                                type="button"
                                onClick={() => handleTransferClick(assetId)}
                                style={{
                                  padding: "6px 12px",
                                  background: "#667eea",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  fontWeight: 600,
                                  fontSize: "12px",
                                  transition: "background 0.2s",
                                  minWidth: "80px",
                                  minHeight: "32px",
                                  display: "inline-block",
                                }}
                                onMouseEnter={(e) =>
                                  (e.target.style.background = "#5568d3")
                                }
                                onMouseLeave={(e) =>
                                  (e.target.style.background = "#667eea")
                                }
                              >
                                Transfer
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalAssets}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(newItemsPerPage) => {
              setItemsPerPage(newItemsPerPage);
              setCurrentPage(1);
            }}
            showItemsPerPage={true}
          />

          {/* Transfer Selected Button - Bottom */}
          {selectedAssets.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
                paddingTop: "16px",
                marginTop: "16px",
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <button
                type="button"
                onClick={handleBulkTransferClick}
                style={{
                  padding: "10px 20px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: "13px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>📤</span>
                <span>Transfer Selected ({selectedAssets.length})</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MyAssetsPage;
